import { validate as validateCryptoAddress } from "multicoin-address-validator";
import { z } from "zod";

import type { CryptoSelectOption } from "../../presentation/CryptoSelect";

export const assetAddressSchema = z.object({
  chain: z.string().min(1, "Chain is required"),
  asset: z.string().min(1, "Asset is required"),
  address: z.string().min(1, "Address is required"),
});

export type AssetAddressFormValues = z.input<typeof assetAddressSchema>;

export const ASSET_ADDRESS_DEFAULTS: AssetAddressFormValues = {
  chain: "",
  asset: "",
  address: "",
};

export const refineAssetAddress = (
  data: AssetAddressFormValues,
  ctx: z.RefinementCtx,
  assetOptionsByChain: Record<string, CryptoSelectOption[]>,
): void => {
  if (!data.chain || !data.address.trim()) {
    return;
  }

  const chainAssets = assetOptionsByChain[data.chain];
  if (!chainAssets) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["chain"],
      message: "This chain is not supported.",
    });
    return;
  }
  if (data.asset && !chainAssets.some((opt) => opt.value === data.asset)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["asset"],
      message: "This asset is not available on the selected chain.",
    });
    return;
  }

  try {
    if (validateCryptoAddress(data.address, data.chain)) {
      return;
    }
  } catch {
    // Validator unavailable for this chain — defer to backend validation.
    // useAddressValidation surfaces a non-blocking double-check notice.
    return;
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ["address"],
    message:
      "This doesn't look like a valid address. Please check for typos or extra spaces.",
  });
};
